import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Send, Loader2, X, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISS_KEY = (sid) => `vidente_rating_dismissed_${sid}`;

/**
 * Inline rating + comment widget used at the bottom of a Vidente Virtual
 * session. The user can edit their rating anytime — every change is logged
 * server-side in `vidente_rating_history` so the admin can see the evolution.
 *
 * The widget can be dismissed manually (X) or hides itself automatically
 * after a successful submit. Dismissal is remembered in sessionStorage so
 * it stays hidden during the rest of the conversation, but reappears on a
 * fresh session.
 *
 * If the admin has posted a reply to this rating, it shows pinned ABOVE the
 * star UI so the user always sees it.
 */
export function VidenteRatingWidget({ sessionId, token }) {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState("");
  const [adminReply, setAdminReply] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (!sessionId) return false;
    try { return sessionStorage.getItem(DISMISS_KEY(sessionId)) === "1"; }
    catch { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY(sessionId), "1"); } catch { /* noop */ }
  };

  useEffect(() => {
    let cancelled = false;
    if (!sessionId || !token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vidente/session/${sessionId}/rating`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const r = data?.rating;
        if (r) {
          setStars(r.stars || 0);
          setComment(r.comment || "");
          if (r.admin_reply) {
            setAdminReply({
              text: r.admin_reply,
              at: r.admin_reply_at,
              by: r.admin_reply_by,
              unread: r.admin_reply_read === false,
            });
            // Mark as read (silent best-effort, fire and forget)
            if (r.admin_reply_read === false) {
              axios.post(
                `${API}/vidente/session/${sessionId}/rating/mark-reply-read`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              ).catch(() => null);
            }
          }
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sessionId, token]);

  const handleSubmit = async () => {
    if (stars < 1) {
      toast.error("Selecciona al menos una estrella");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/vidente/session/${sessionId}/rating`,
        { stars, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Gracias por tu calificación ✨");
      // Auto-hide after submit
      dismiss();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center text-xs text-neutral-500 py-4">Cargando calificación…</div>;
  }

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          key="rating-widget"
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -8, height: 0, marginTop: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          data-testid="vidente-rating-widget"
          className="mt-6 border border-[#d4af37]/20 rounded-2xl p-5 bg-gradient-to-b from-black/40 to-black/20 relative overflow-hidden"
        >
          <button
            onClick={dismiss}
            data-testid="rating-dismiss"
            aria-label="Ocultar calificación"
            title="Ocultar"
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <h3 className="text-sm uppercase tracking-widest text-[#d4af37] mb-1 font-semibold pr-8">
            ¿Cómo estuvo tu sesión con CrisAngel Astral?
          </h3>
          <p className="text-xs text-neutral-500 mb-4">
            Tu opinión nos ayuda a mejorar. Puedes cambiar tu calificación más tarde.
          </p>

          {adminReply && (
            <div
              data-testid="rating-admin-reply"
              className="mb-4 rounded-xl border border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/10 via-purple-900/10 to-black p-4 relative"
            >
              {adminReply.unread && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                  Nuevo
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-[#d4af37]/20 border border-[#d4af37]/40 flex items-center justify-center">
                  <MessageCircle className="w-3.5 h-3.5 text-[#d4af37]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#d4af37] font-semibold">
                    Respuesta de {adminReply.by || "CrisAngel"}
                  </div>
                  {adminReply.at && (
                    <div className="text-[9px] text-neutral-500">{new Date(adminReply.at).toLocaleString()}</div>
                  )}
                </div>
              </div>
              <p className="text-sm text-neutral-100 whitespace-pre-wrap mt-1.5">{adminReply.text}</p>
            </div>
          )}

          <div
            className="flex items-center gap-1.5 mb-4"
            onMouseLeave={() => setHoverStars(0)}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <motion.button
                key={n}
                data-testid={`rating-star-${n}`}
                onMouseEnter={() => setHoverStars(n)}
                onClick={() => setStars(n)}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                className="p-1"
                aria-label={`${n} estrellas`}
              >
                <Star
                  className={`w-7 h-7 transition-colors ${
                    n <= (hoverStars || stars)
                      ? "text-[#d4af37] fill-[#d4af37]"
                      : "text-neutral-700"
                  }`}
                />
              </motion.button>
            ))}
            {stars > 0 && (
              <span className="ml-2 text-xs text-[#d4af37]">{stars}/5</span>
            )}
          </div>

          <textarea
            data-testid="rating-comment"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Cuéntale a CrisAngel Astral qué te pareció la consulta (opcional)…"
            className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/40 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
          />

          <button
            data-testid="rating-submit"
            onClick={handleSubmit}
            disabled={saving || stars < 1}
            className="mt-3 w-full btn-gold py-2.5 rounded-full uppercase text-xs tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {saving ? "Guardando…" : "Enviar calificación"}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
